import { Component, Inject, OnInit } from '@angular/core';
import { WelcomePageComponent } from '../../common/welcome-page/welcome-page.component';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { AuthenticationService } from '../../services/authentication.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { UserManagementService } from '../../services/user-management.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { AUTH_PROVIDER_TOKEN, AuthProvider } from '../../core/tokens/auth-provider.token';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [WelcomePageComponent, MatButtonModule, MatDialogModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent extends Destroyable(Object) implements OnInit {
  get isOktaAuthUsed(): boolean {
    return this.authProvider === 'Okta';
  }

  constructor(
    private authenticationService: AuthenticationService,
    private tokenStorageService: TokenStorageService,
    private userManagementService: UserManagementService,
    private router: Router,
    private dialog: MatDialog,
    @Inject(AUTH_PROVIDER_TOKEN) private authProvider: AuthProvider,
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.tokenStorageService.removeTokens();
    this.userManagementService.removeUserInfo();
    this.dialog.closeAll();

    if (this.authProvider === 'Entra') {
      // Wait until any in-progress MSAL interaction (e.g. the return redirect being
      // processed in APP_INITIALIZER) has settled before starting a new one.
      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status) => status === InteractionStatus.None),
          take(1),
          this.takeUntilDestroyed(),
        )
        .subscribe(() => {
          if (!this.msalService.instance.getAllAccounts().length) {
            const scopes = environment.msalScopeUri ? [environment.msalScopeUri] : [];
            this.msalService.loginRedirect({ scopes });
          }
        });
    }
  }

  login([email, password]: [string, string | null]): void {
    if (this.authProvider === 'Entra') {
      const scopes = environment.msalScopeUri ? [environment.msalScopeUri] : [];
      this.msalService.loginRedirect({ scopes });
      return;
    }

    if (this.isOktaAuthUsed) {
      this.oktaAuth.signInWithRedirect();
      this.oktaAuth.handleLoginRedirect().then(() => {
        this.router.navigate(['/']);
      });
      return;
    }

    this.authenticationService
      .login(email, password!)
      .pipe(this.takeUntilDestroyed())
      .subscribe((token) => {
        this.tokenStorageService.saveTokens(token);
        this.router.navigate(['/dashboard']);
      });
  }
}
